import { IconButton, Stack, Text } from "@fluentui/react";
import Box from "@mui/material/Box";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import i18next from "../../i18n/i18n";
import styles from "./Answer.module.css";
import { AppType } from "../../api";
import React, { useEffect, useState } from "react";
import { appInfo, userInfo } from "../../authConfig";
import { AnswerIcon } from "./AnswerIcon";
import { LockIcon } from "../LockIcon/LockIcon";
import { resolveProductLocale } from "../../pages/chat/utils/sessionHelpers";
interface Props {
    selectedAppType: string;
    isLoading: boolean;
    isStreaming: boolean;
    answer: any;
    onRetry: () => void;
    onGenerateMissingResponse?: (appType: AppType) => void;
    setSelectedAppType: (appType: AppType) => void;
    onTabClicked: () => void;
    primaryId: string | number;
    isSelected: boolean;
    doctype: boolean;
    isFeedbackPage?: boolean;
    conversationDate?: string | null;
}

export const AnswerRegenarate = ({
    selectedAppType,
    isLoading,
    isStreaming,
    answer,
    onRetry,
    onGenerateMissingResponse,
    setSelectedAppType,
    onTabClicked,
    primaryId,
    isSelected,
    doctype,
    isFeedbackPage,
    conversationDate
}: Props) => {
    const [productType, setProductType] = useState<{ manual_key: string; faq_key: string }>({
        manual_key: "0",
        faq_key: "0"
    });
    const isWap = String(userInfo?.isWap).toLowerCase() === "true";
    const useAll = appInfo?.use_all_tab === true;
    const hideTabs = !isWap && useAll;
    const showAllTab = isWap && useAll;
    const isFeedbackRoute = String(window.location.pathname).includes("/assistant/feedbacks");
    const isFeedbackContext = isFeedbackPage === true || isFeedbackRoute;
    const getContextForAppType = (appType: string) => answer?.[appType]?.context;
    const selectedAppContext = getContextForAppType(selectedAppType);
    const selectedAppAnswer = answer?.[selectedAppType];
    const selectedConversationDate = selectedAppAnswer
        ? (selectedAppAnswer.conversation_date ?? selectedAppContext?.conversationDate ?? conversationDate)
        : null;

    useEffect(() => {
        const productTypeValue = (() => {
            const getProductFilter = (appType: string) => answer[appType]?.context?.product_filter;

            if (selectedAppType && answer[selectedAppType]) {
                const filter = getProductFilter(selectedAppType);
                if (filter) return filter;
            }
            const fallbackOrder = ["ALL", "FAQ", "Manuals"];
            for (const appType of fallbackOrder) {
                if (answer[appType]) {
                    const filter = getProductFilter(appType);
                    if (filter) return filter;
                }
            }
            return { manual_key: "0", faq_key: "0" };
        })();
        const selectedLocale = sessionStorage.getItem("selectedLocale") ?? "ja";
        const resolvedLocale = resolveProductLocale(selectedLocale, appInfo);
        const filteredSelectedProduct = appInfo.product_categories[resolvedLocale]?.filter((item: any) =>
            selectedAppType === "ALL"
                ? item.faq_key === productTypeValue.faq_key || item.manual_key === productTypeValue.manual_key
                : selectedAppType !== "FAQ"
                  ? item.faq_key === productTypeValue.faq_key
                  : item.manual_key === productTypeValue.manual_key
        );
        if (filteredSelectedProduct && filteredSelectedProduct?.length > 0) {
            if (productTypeValue.manual_key === "" && productTypeValue.faq_key !== "") {
                productTypeValue.manual_key = filteredSelectedProduct[0].manual_key;
            }
            if (productTypeValue.manual_key !== "" && productTypeValue.faq_key === "") {
                productTypeValue.faq_key = filteredSelectedProduct[0].faq_key;
            }
        }
        setProductType(productTypeValue);
    }, [answer, selectedAppType]);

    const handleTabChangeIndex = (event: React.SyntheticEvent, newValue: AppType) => {
        if (!isFeedbackContext && (isLoading || isStreaming)) {
            event.preventDefault();
        } else {
            setSelectedAppType(newValue);
            if (!isFeedbackContext && !answer?.[newValue]) {
                onGenerateMissingResponse?.(newValue);
            }
        }
    };

    const responseInfoMessageKey = isFeedbackContext
        ? selectedAppType === "FAQ" && productType.faq_key === ""
            ? "answer.faqUnavailableForSelectedProduct"
            : selectedAppType === "Manuals" && productType.manual_key === ""
              ? "answer.manualsUnavailableForSelectedProduct"
              : "answer.responseNotGenerated"
        : selectedAppType === "ALL"
          ? "answer.regenerateResponseInfoText"
          : selectedAppType === "FAQ" && productType.faq_key === ""
            ? "answer.faqUnavailableForSelectedProduct"
            : selectedAppType === "Manuals" && productType.manual_key === ""
              ? "answer.manualsUnavailableForSelectedProduct"
              : "answer.regenerateResponseInfoText";
    const isSelectedProductAvailable = selectedAppType === "FAQ" ? productType.faq_key !== "" : productType.manual_key !== "";
    const showRegenerateButton = !isFeedbackContext && (selectedAppType === "ALL" || isSelectedProductAvailable);
    const isRegenerateDisabled = isLoading || isStreaming || (selectedAppType !== "ALL" && !isSelectedProductAvailable);

    return (
        <Stack
            id={String(primaryId)}
            className={`${styles.answerContainer} ${isSelected && styles.selected}`}
            style={{
                ...(doctype && { backgroundColor: "rgb(240, 255, 240)" })
            }}
            verticalAlign="space-between"
        >
            <Stack.Item>
                <div
                    style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "8px",
                        gap: "12px"
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: "6px" }}>
                        <AnswerIcon
                            language={selectedAppContext?.language}
                            documenttype={selectedAppContext?.document_category}
                            chatModel={selectedAppContext?.chat_model}
                            reasoningEffort={selectedAppContext?.reasoning_effort}
                            product={selectedAppContext?.product_filter}
                            productVersion={selectedAppContext?.productVersion}
                            sub_product={selectedAppContext?.sub_product_filter}
                            feedbackCorpId={selectedAppContext?.feedback_corp_id}
                            feedbackUser={selectedAppContext?.feedback_user}
                            CorpId={selectedAppContext?.corpId}
                            UserId={selectedAppContext?.userId}
                            isFeedbackPage={isFeedbackPage}
                            apptype={selectedAppType}
                            conversationDate={selectedConversationDate}
                        />
                        {doctype && <LockIcon />}
                    </div>
                    <div style={{ flex: 1 }}>
                        {hideTabs ? null : (
                            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                                <Tabs
                                    className={styles.answerText}
                                    onChange={handleTabChangeIndex}
                                    value={selectedAppType}
                                    aria-label="basic tabs example"
                                    style={{ padding: "0px" }}
                                >
                                    {showAllTab && <Tab label={i18next.t("chat.ALL")} value="ALL" className={styles["tab-root"]} onClick={onTabClicked} />}
                                    <Tab label={i18next.t("chat.faq")} value="FAQ" className={styles["tab-root"]} onClick={onTabClicked} />
                                    <Tab label={i18next.t("chat.manuals")} value="Manuals" className={styles["tab-root"]} onClick={onTabClicked} />
                                </Tabs>
                            </Box>
                        )}
                    </div>
                </div>
            </Stack.Item>
            <Stack.Item grow>
                <Box sx={{ width: "100%", marginTop: "5%", display: "flex", flexDirection: "row" }}>
                    <Text style={{ marginTop: "4px", color: "#1976d2" }}>{i18next.t(responseInfoMessageKey)}</Text>
                    {showRegenerateButton && (
                        <IconButton
                            disabled={isRegenerateDisabled}
                            style={{ color: "#1976d2", float: "right" }}
                            iconProps={{ iconName: "Refresh" }}
                            onClick={onRetry}
                        />
                    )}
                </Box>
            </Stack.Item>
        </Stack>
    );
};
